import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Badge } from '../ui/badge';
import { Hash, AlertCircle } from 'lucide-react';
import { crcToolApi } from '../../lib/toolsApi';

const WIDTHS = [8, 16, 32];

const CrcCalculator = () => {
  const [dataInput, setDataInput] = useState('00 01 02 03 04 05 06 07 08 09');
  const [width, setWidth] = useState(16);
  const [presetId, setPresetId] = useState('ccitt-false');
  const [custom, setCustom] = useState({ poly: '1021', init: 'FFFF', xorout: '0000', refin: false, refout: false });
  const [useCustom, setUseCustom] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [presetsByWidth, setPresetsByWidth] = useState(null);

  useEffect(() => {
    crcToolApi.getPresets()
      .then(setPresetsByWidth)
      .catch((e) => setError(e.message));
  }, []);

  const presets = presetsByWidth ? presetsByWidth[width] : null;
  const activePreset = presets ? (presets.find((p) => p.id === presetId) || presets[0]) : null;

  const handleWidthChange = (w) => {
    const wNum = Number(w);
    setWidth(wNum);
    if (presetsByWidth) setPresetId(presetsByWidth[wNum][0].id);
    setResult(null);
  };

  const calculate = async () => {
    setError(null);
    setResult(null);
    try {
      const params = useCustom
        ? {
            width,
            poly: parseInt(custom.poly, 16),
            init: parseInt(custom.init, 16),
            xorout: parseInt(custom.xorout, 16),
            refin: custom.refin,
            refout: custom.refout,
          }
        : { width, poly: activePreset.poly, init: activePreset.init, xorout: activePreset.xorout, refin: activePreset.refin, refout: activePreset.refout };

      if (useCustom && (Number.isNaN(params.poly) || Number.isNaN(params.init) || Number.isNaN(params.xorout))) {
        throw new Error('Custom polynomial, init, and XOR-out must be valid hex values.');
      }

      const outcome = await crcToolApi.computeCrc(dataInput, params);
      setResult({ ...outcome, params });
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="technical-card lg:col-span-3">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Hash className="h-5 w-5 text-autoxium-blue" />
            CRC Calculator
          </CardTitle>
          <CardDescription>
            Compute CRC-8, CRC-16, or CRC-32 over arbitrary data using selectable standard polynomials.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Data (hex bytes)
            </Label>
            <Textarea
              value={dataInput}
              onChange={(e) => setDataInput(e.target.value)}
              placeholder="e.g. 00 01 02 03 or 313233343536373839"
              className="font-mono text-sm min-h-[90px]"
              data-testid="crc-data-input"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">CRC Width</Label>
              <Select value={String(width)} onValueChange={handleWidthChange}>
                <SelectTrigger data-testid="crc-width-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WIDTHS.map((w) => (
                    <SelectItem key={w} value={String(w)}>CRC-{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Polynomial Preset</Label>
              <Select
                value={useCustom ? 'custom' : presetId}
                onValueChange={(v) => {
                  if (v === 'custom') setUseCustom(true);
                  else { setUseCustom(false); setPresetId(v); }
                }}
              >
                <SelectTrigger data-testid="crc-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(presets || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {useCustom ? (
            <div className="grid sm:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-md">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Poly (hex)</Label>
                <Input value={custom.poly} onChange={(e) => setCustom({ ...custom, poly: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Init (hex)</Label>
                <Input value={custom.init} onChange={(e) => setCustom({ ...custom, init: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">XOR-out (hex)</Label>
                <Input value={custom.xorout} onChange={(e) => setCustom({ ...custom, xorout: e.target.value })} className="font-mono" />
              </div>
              <label className="flex items-center gap-2 text-sm col-span-1">
                <input type="checkbox" checked={custom.refin} onChange={(e) => setCustom({ ...custom, refin: e.target.checked })} />
                Reflect input
              </label>
              <label className="flex items-center gap-2 text-sm col-span-1">
                <input type="checkbox" checked={custom.refout} onChange={(e) => setCustom({ ...custom, refout: e.target.checked })} />
                Reflect output
              </label>
            </div>
          ) : activePreset ? (
            <div className="flex flex-wrap gap-2 font-mono text-xs text-muted-foreground">
              <Badge variant="outline">poly=0x{activePreset.poly.toString(16).toUpperCase()}</Badge>
              <Badge variant="outline">init=0x{activePreset.init.toString(16).toUpperCase()}</Badge>
              <Badge variant="outline">xorout=0x{activePreset.xorout.toString(16).toUpperCase()}</Badge>
              <Badge variant="outline">refin={String(activePreset.refin)}</Badge>
              <Badge variant="outline">refout={String(activePreset.refout)}</Badge>
            </div>
          ) : null}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid="crc-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <Button onClick={calculate} className="btn-premium w-full" disabled={!useCustom && !activePreset} data-testid="crc-calculate-btn">
            Calculate
          </Button>
        </CardContent>
      </Card>

      <Card className="technical-card lg:col-span-2 h-fit">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Result</CardTitle>
        </CardHeader>
        <CardContent>
          {!result ? (
            <p className="text-sm text-muted-foreground">Enter data and click Calculate to see the CRC.</p>
          ) : (
            <div className="space-y-4" data-testid="crc-result">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Hex</p>
                <p className="font-mono text-2xl font-bold text-autoxium-blue">{result.hex}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-structure">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Decimal</p>
                  <p className="font-mono text-sm">{result.decimal}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Bytes Processed</p>
                  <p className="font-mono text-sm">{result.byteCount}</p>
                </div>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Binary</p>
                <p className="font-mono text-xs break-all text-muted-foreground">{result.binary}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CrcCalculator;
